import _assign from "object-assign";

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import { useEffect, useMemo, useCallback } from 'react';
import warning from 'warning';
import { useListStateContext } from './ListStateContext';
import { useListItemContext } from './ListItemContext';
export function useListItem() {
  var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  validateProps(props);
  var context = useListItemContext();

  var _useListStateContext = useListStateContext(),
      state = _useListStateContext.state,
      helpers = _useListStateContext.helpers;

  var setHighlightedIndex = helpers.setHighlightedIndex,
      setFocusVisible = helpers.setFocusVisible,
      handleSelect = helpers.handleSelect;
  var quickSelection = state.quickSelection,
      focusVisible = state.focusVisible,
      highlightedIndex = state.highlightedIndex,
      selectedItem = state.selectedItem,
      isDisabled = state.isDisabled,
      isMultiSelect = state.isMultiSelect;

  var _props$checkable = props.checkable,
      checkable = _props$checkable === void 0 ? context.checkable : _props$checkable,
      index = props.index,
      item = props.item,
      onClick = props.onClick,
      onMouseMove = props.onMouseMove,
      onMouseDown = props.onMouseDown,
      _props$density = props.density,
      density = _props$density === void 0 ? context.density : _props$density,
      _props$id = props.id,
      id = _props$id === void 0 ? context.getItemId(index) : _props$id,
      _props$itemHeight = props.itemHeight,
      itemHeight = _props$itemHeight === void 0 ? context.getItemHeight(index) : _props$itemHeight,
      _props$itemToString = props.itemToString,
      itemToString = _props$itemToString === void 0 ? context.itemToString : _props$itemToString,
      _props$itemTextHighli = props.itemTextHighlightPattern,
      itemTextHighlightPattern = _props$itemTextHighli === void 0 ? context.itemTextHighlightPattern : _props$itemTextHighli,
      _props$disabled = props.disabled,
      disabled = _props$disabled === void 0 ? item.disabled || isDisabled : _props$disabled,
      ariaPropsProp = props.ariaProps,
      styleProp = props.style,
      restProps = _objectWithoutProperties(props, ["checkable", "index", "item", "onClick", "onMouseMove", "onMouseDown", "density", "id", "itemHeight", "itemToString", "itemTextHighlightPattern", "disabled", "ariaProps", "style"]);

  var style = useMemo(function () {
    return _assign({
      height: itemHeight
    }, styleProp);
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [itemHeight, JSON.stringify(styleProp)]);
  var highlighted = index === highlightedIndex;
  var selected = isMultiSelect ? selectedItem.indexOf(item) !== -1 : item === selectedItem;
  var quickSelected = index === 0 && quickSelection;
  var handleClick = useCallback(function (event) {
    handleSelect(event, index, item);

    if (onClick) {
      onClick(event);
    }
  }, [handleSelect, index, item, onClick]);
  var handleMouseMove = useCallback(function (event) {
    setHighlightedIndex(index);
    setFocusVisible(false);

    if (onMouseMove) {
      onMouseMove(event);
    }
  }, [index, setFocusVisible, setHighlightedIndex, onMouseMove]);

  var handleMouseDown = function handleMouseDown(event) {
    if (context.disableMouseDown) {
      event.preventDefault();
    } else if (onMouseDown) {
      onMouseDown(event);
    }
  };

  var eventHandlers = {
    onClick: handleClick,
    onMouseMove: handleMouseMove,
    onMouseDown: handleMouseDown
  };

  var ariaProps = _assign({
    role: 'option'
  }, disabled && {
    'aria-disabled': true
  }, ariaPropsProp);

  if (selected) {
    ariaProps["".concat(isMultiSelect ? 'aria-selected' : 'aria-checked')] = true;
  }

  return {
    item: item,
    itemToString: itemToString,
    itemProps: _assign({
      'data-option-index': index,
      checkable: checkable,
      id: id,
      style: style,
      density: density,
      disabled: disabled,
      selected: selected,
      highlighted: highlighted,
      quickSelected: quickSelected,
      itemTextHighlightPattern: itemTextHighlightPattern,
      focusVisible: focusVisible && highlighted,
      tooltipText: itemToString(item)
    }, ariaProps, restProps, disabled ? {} : eventHandlers)
  };
}
export var useVirtualizedListItem = function useVirtualizedListItem() {
  var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var index = props.index,
      data = props.data,
      _props$style = props.style,
      style = _props$style === void 0 ? {} : _props$style; // Filter out inline width added by `react-window` so that it can only be defined using css.

  var _unusedWidth = style.width,
      itemHeight = style.height,
      restStyle = _objectWithoutProperties(style, ["width", "height"]);

  return useListItem({
    index: index,
    itemHeight: itemHeight,
    style: restStyle,
    item: data[index],
    ariaProps: {
      'aria-posinset': index + 1,
      'aria-setsize': data.length
    }
  });
};

var validateProps = function validateProps(props) {
  if (process.env.NODE_ENV !== 'production') {
    var index = props.index,
        item = props.item;
    /* eslint-disable react-hooks/rules-of-hooks */

    useEffect(function () {
      warning(item !== undefined, 'useListItem needs `item`.');
      warning(index !== undefined, "useListItem needs to know item's index.");
    }, [index, item]);
    /* eslint-enable react-hooks/rules-of-hooks */
  }
};